import React from 'react';
import PublicNav from '../../components/PublicNav/PublicNav';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import './CookiePolicy.css';

const CookiePolicy = () => {
  return (
    <div className="cookie-policy-page">
      <PublicNav />
      
      <div className="cookie-policy-container">
        <div className="cookie-policy-header">
          <h1>Cookie Policy</h1>
          <p className="last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="cookie-policy-content">
          <section className="policy-section">
            <h2>1. Introduction</h2>
            <p>
              This Cookie Policy explains how ProjectPro ("we", "us", or "our") uses cookies and similar 
              technologies when you visit our website and use our project portfolio management platform. 
              This policy provides you with clear and comprehensive information about the cookies we use 
              and the purposes for using them.
            </p>
          </section>

          <section className="policy-section">
            <h2>2. What Are Cookies?</h2>
            <p>
              Cookies are small text files that are placed on your computer or mobile device when you 
              visit a website. They are widely used to make websites work more efficiently and provide 
              information to the website owners.
            </p>
            <p>
              Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your device 
              after you close your browser, while session cookies are deleted when you close your browser.
            </p>
          </section>

          <section className="policy-section">
            <h2>3. Types of Cookies We Use</h2>
            
            <div className="cookie-type">
              <h3>3.1 Essential Cookies</h3>
              <p>
                These cookies are necessary for the website to function properly. They enable core 
                functionality such as security, network management, and accessibility. You cannot opt 
                out of these cookies.
              </p>
              <ul>
                <li><strong>Authentication cookies:</strong> Keep you logged in as you navigate through our platform</li>
                <li><strong>Security cookies:</strong> Help detect and prevent security risks</li>
                <li><strong>Load balancing cookies:</strong> Ensure the website performs optimally</li>
              </ul>
            </div>

            <div className="cookie-type">
              <h3>3.2 Functional Cookies</h3>
              <p>
                These cookies enable enhanced functionality and personalization, such as remembering 
                your preferences and settings.
              </p>
              <ul>
                <li><strong>Preference cookies:</strong> Remember your language, region, and display preferences</li>
                <li><strong>User interface cookies:</strong> Store your customization choices</li>
                <li><strong>Feature cookies:</strong> Remember features you've enabled or disabled</li>
              </ul>
            </div>

            <div className="cookie-type">
              <h3>3.3 Analytics Cookies</h3>
              <p>
                These cookies help us understand how visitors interact with our website by collecting 
                and reporting information anonymously.
              </p>
              <ul>
                <li><strong>Google Analytics:</strong> Tracks website usage and user behavior</li>
                <li><strong>Performance monitoring:</strong> Helps us identify and fix technical issues</li>
                <li><strong>Usage statistics:</strong> Provides insights into feature adoption and usage patterns</li>
              </ul>
            </div>

            <div className="cookie-type">
              <h3>3.4 Marketing Cookies</h3>
              <p>
                These cookies track your online activity to help advertisers deliver more relevant 
                advertising or to limit how many times you see an ad.
              </p>
              <ul>
                <li><strong>Advertising cookies:</strong> Used to show you relevant ads based on your interests</li>
                <li><strong>Social media cookies:</strong> Enable social media features and track social sharing</li>
                <li><strong>Retargeting cookies:</strong> Show you relevant ads on other websites</li>
              </ul>
            </div>
          </section>

          <section className="policy-section">
            <h2>4. Specific Cookies We Use</h2>
            <div className="cookie-table-wrapper">
              <table className="cookie-table">
                <thead>
                  <tr>
                    <th>Cookie Name</th>
                    <th>Purpose</th>
                    <th>Type</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>auth_token</td>
                    <td>Maintains your login session</td>
                    <td>Essential</td>
                    <td>Session</td>
                  </tr>
                  <tr>
                    <td>user_preferences</td>
                    <td>Stores your UI preferences</td>
                    <td>Functional</td>
                    <td>1 year</td>
                  </tr>
                  <tr>
                    <td>_ga</td>
                    <td>Google Analytics tracking</td>
                    <td>Analytics</td>
                    <td>2 years</td>
                  </tr>
                  <tr>
                    <td>_gid</td>
                    <td>Google Analytics tracking</td>
                    <td>Analytics</td>
                    <td>24 hours</td>
                  </tr>
                  <tr>
                    <td>language</td>
                    <td>Remembers your language preference</td>
                    <td>Functional</td>
                    <td>1 year</td>
                  </tr>
                  <tr>
                    <td>theme</td>
                    <td>Stores your theme preference (light/dark)</td>
                    <td>Functional</td>
                    <td>1 year</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="policy-section">
            <h2>5. Third-Party Cookies</h2>
            <p>
              In addition to our own cookies, we may also use various third-party cookies to report 
              usage statistics of the service and deliver advertisements on and through the service.
            </p>
            <ul>
              <li><strong>Google Analytics:</strong> For website analytics and performance monitoring</li>
              <li><strong>Social Media Platforms:</strong> For social sharing and login functionality</li>
              <li><strong>Payment Processors:</strong> For secure payment processing</li>
              <li><strong>Customer Support Tools:</strong> For providing customer support services</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>6. How to Control Cookies</h2>
            <p>
              You have the right to decide whether to accept or reject cookies. You can exercise your 
              cookie preferences by:
            </p>
            
            <div className="control-method">
              <h3>6.1 Browser Settings</h3>
              <p>
                Most web browsers allow you to control cookies through their settings preferences. 
                However, if you limit the ability of websites to set cookies, you may worsen your 
                overall user experience.
              </p>
              <ul>
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Cookies and website data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
              </ul>
            </div>

            <div className="control-method">
              <h3>6.2 Cookie Consent Manager</h3>
              <p>
                When you first visit our website, you'll see a cookie consent banner where you can 
                choose which types of cookies to accept. You can change your preferences at any time 
                by clicking the "Cookie Settings" link in the footer.
              </p>
            </div>

            <div className="control-method">
              <h3>6.3 Opt-Out Links</h3>
              <ul>
                <li>
                  <strong>Google Analytics:</strong> 
                  <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                    Google Analytics Opt-out Browser Add-on
                  </a>
                </li>
                <li>
                  <strong>Network Advertising Initiative:</strong> 
                  <a href="http://www.networkadvertising.org/choices/" target="_blank" rel="noopener noreferrer">
                    NAI Opt-out Tool
                  </a>
                </li>
              </ul>
            </div>
          </section>

          <section className="policy-section">
            <h2>7. Do Not Track Signals</h2>
            <p>
              Some browsers include a "Do Not Track" (DNT) feature that signals to websites that you 
              visit that you do not want to have your online activity tracked. We currently do not 
              respond to DNT signals, but we are committed to providing you with meaningful choices 
              about the information collected on our website.
            </p>
          </section>

          <section className="policy-section">
            <h2>8. Updates to This Cookie Policy</h2>
            <p>
              We may update this Cookie Policy from time to time to reflect changes in our practices 
              or for other operational, legal, or regulatory reasons. We will notify you of any 
              material changes by posting the new Cookie Policy on this page and updating the "Last 
              Updated" date.
            </p>
          </section>

          <section className="policy-section">
            <h2>9. Contact Us</h2>
            <p>
              If you have any questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> privacy@projectpro.com</p>
              <p><strong>Address:</strong> Mumbai, Maharashtra, India</p>
              <p><strong>Support:</strong> support@projectpro.com</p>
            </div>
          </section>

          <section className="policy-section consent-section">
            <h2>10. Your Consent</h2>
            <p>
              By using our website, you consent to our use of cookies in accordance with this Cookie 
              Policy. If you do not agree to our use of cookies, you should set your browser settings 
              accordingly or not use our website.
            </p>
          </section>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default CookiePolicy;


